import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { lexicalService } from '../services/lexical.service';
import type { GetLexicalWordsParams } from '../services/lexical.service';
import { vocabularyService } from '../services/vocabulary.service';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const lexicalKeys = {
  all: ['lexical'] as const,
  languages: () => [...lexicalKeys.all, 'languages'] as const,
  stats: (language?: string) => [...lexicalKeys.all, 'stats', language] as const,
  words: (params?: GetLexicalWordsParams) => [...lexicalKeys.all, 'words', params] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetch aggregate word exposure statistics for the current user.
 * Optional language filter (e.g. "Japanese").
 */
export const useLexicalStats = (language?: string) => {
  return useQuery({
    queryKey: lexicalKeys.stats(language),
    queryFn: () => lexicalService.getStats(language),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Fetch the distinct languages present in the current user's lexical data.
 */
export const useLexicalLanguages = () => {
  return useQuery({
    queryKey: lexicalKeys.languages(),
    queryFn: () => lexicalService.getLanguages(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * Fetch a paginated list of words the current user has been exposed to.
 */
export const useLexicalWords = (params?: GetLexicalWordsParams) => {
  return useQuery({
    queryKey: lexicalKeys.words(params),
    queryFn: () => lexicalService.getWords(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Trigger a backfill of all existing transcripts for the current user.
 * Shows a toast on completion with the result summary.
 */
export const useLexicalBackfill = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (language?: string) => {
      // Run lexical backfill first so lexical_words is fully populated,
      // then resolve lexical_word_id on vocab words against those entries.
      const result = await lexicalService.backfill(language);
      await vocabularyService.backfillLexical();
      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: lexicalKeys.all });
      toast.success(
        `Backfill complete: ${result.wordsUpserted.toLocaleString()} words across ${result.segmentsProcessed} segments`
      );
    },
    onError: () => {
      toast.error('Backfill failed. Please try again.');
    },
  });
};
